import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { AuditingPartItem, AuditingPartsRequest, AuditingPartsResponse } from '../../types/reporting';

const getAuditingParts = async (
    sessionId: string,
    input: AuditingPartsRequest
): Promise<AuditingPartsResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/auditing/parts`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

// Options for server-side filters/pagination/search/sort
export interface UseAuditingPartsOptions {
    locationIds?: string[];
    insuranceIds?: string[];
    makes?: string[];
    partTypes?: string[];
    vendors?: string[];
    /** Explicit order IDs (e.g. from pivot drilldown). */
    orderIds?: string[];
    /** Matches global reporting timeframe (e.g. last_7_days, custom). */
    preset?: string;
    excludeEvs?: boolean;
    page?: number;
    pageSize?: number;
    searchQuery?: string;
    sortBy?: string | null;
    sortOrder?: 'asc' | 'desc';
    rangeFilters?: Record<string, { min: string; max: string }>;
    /** When false, keep cache but do not fetch (inactive reporting tab). */
    enabled?: boolean;
}

const stableIdsKey = (ids?: string[]) =>
    ids?.length ? JSON.stringify([...ids].sort()) : '';
const stableObjectKey = (obj?: Record<string, unknown>) =>
    obj ? JSON.stringify(Object.keys(obj).sort().reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = obj[key];
        return acc;
    }, {})) : '';

/** Server hard-caps pageSize at 200 in getAuditingParts. */
const AUDITING_PARTS_MAX_PAGE_SIZE = 200;

/**
 * Fetches every auditing part matching the given filters by paging through the
 * endpoint in batches. Used for "export all" flows where the on-screen pagination
 * should be ignored.
 */
export const fetchAllAuditingParts = async (
    sessionId: string,
    startDate: string,
    endDate: string,
    options?: Omit<UseAuditingPartsOptions, 'page' | 'pageSize'>,
    batchSize = AUDITING_PARTS_MAX_PAGE_SIZE
): Promise<AuditingPartItem[]> => {
    const all: AuditingPartItem[] = [];
    let page = 1;
    const requestedPageSize = Math.min(batchSize, AUDITING_PARTS_MAX_PAGE_SIZE);
    // Safety cap to avoid an unbounded loop if pagination metadata is missing.
    for (let i = 0; i < 500; i++) {
        const res = await getAuditingParts(sessionId, {
            startDate,
            endDate,
            preset: options?.preset,
            locationIds: options?.locationIds,
            insuranceIds: options?.insuranceIds,
            makes: options?.makes,
            partTypes: options?.partTypes,
            vendors: options?.vendors,
            orderIds: options?.orderIds,
            excludeEvs: options?.excludeEvs,
            page,
            pageSize: requestedPageSize,
            searchQuery: options?.searchQuery,
            sortBy: options?.sortBy ?? undefined,
            sortOrder: options?.sortOrder,
            rangeFilters: options?.rangeFilters,
        });
        const batch = res.parts ?? [];
        all.push(...batch);
        // Prefer server totalPages; fall back to effective page size (API may clamp).
        const totalPages = res.totalPages ?? 1;
        const effectivePageSize = res.pageSize ?? requestedPageSize;
        if (page >= totalPages || batch.length === 0 || batch.length < effectivePageSize) break;
        page += 1;
    }
    return all;
};

export const useAuditingParts = (
    startDate: string,
    endDate: string,
    options?: UseAuditingPartsOptions
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: [
            'reporting',
            'auditing',
            'parts',
            startDate,
            endDate,
            options?.preset ?? '',
            stableIdsKey(options?.locationIds),
            stableIdsKey(options?.insuranceIds),
            stableIdsKey(options?.makes),
            stableIdsKey(options?.partTypes),
            stableIdsKey(options?.vendors),
            stableIdsKey(options?.orderIds),
            options?.excludeEvs ?? false,
            options?.page ?? 1,
            options?.pageSize ?? 50,
            options?.searchQuery ?? '',
            options?.sortBy ?? '',
            options?.sortOrder ?? '',
            stableObjectKey(options?.rangeFilters),
        ],
        queryFn: () =>
            getAuditingParts(sessionContext?.sessionId ?? '', {
                startDate,
                endDate,
                preset: options?.preset,
                locationIds: options?.locationIds,
                insuranceIds: options?.insuranceIds,
                makes: options?.makes,
                partTypes: options?.partTypes,
                vendors: options?.vendors,
                orderIds: options?.orderIds,
                excludeEvs: options?.excludeEvs,
                page: options?.page,
                pageSize: options?.pageSize,
                searchQuery: options?.searchQuery,
                sortBy: options?.sortBy ?? undefined,
                sortOrder: options?.sortOrder,
                rangeFilters: options?.rangeFilters,
            }),
        enabled: (options?.enabled ?? true) && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
    });
};
